import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';

import statusSelectors from 'state/status/selectors';

import routes from 'utils/routes';

import Login from 'pages/Login';
import LoginRecover from 'pages/LoginRecover';
import Invitations from 'pages/Invitations';
import Logout from 'pages/Logout';
import NewOrder from 'refactored/pages/newOrder';
import Summary from 'pages/Summary';
import CreditCard from 'pages/CreditCard';
import PaymentData from 'pages/PaymentData';
import OrdersPage from 'refactored/pages/ordersPage';
import Billing from 'refactored/pages/billingPage';
import Settings from 'refactored/pages/settingsPage';
import Addresses from 'refactored/pages/addressesPage';
import OrderDetails from 'pages/OrderDetails';
import UtmCampaign from 'pages/UtmCampaign';
import ErrorPage from 'pages/ErrorPage';
import OrganizationMembers from 'refactored/pages/organizationMembersPage';
import TrackingPage from 'pages/Tracking';
import DashboardPage from 'refactored/pages/dashboardPage';

import { selectors as settingsSelectors } from 'state/settings';
import { actions as uiActions } from 'state/ui';
import useShowNewSoForm from 'refactored/features/soForm/useShowNewSoForm';

import NewShipperOrder from 'refactored/pages/NewShipperOrder';
import { IS_TENANT, SERVICES, isTenantByName } from 'tenant/utils';
import EmergenciesModule from 'refactored/features/emergencies/emergencies-module';
import { selectors as clientSelectors, constants } from 'state/clientConfig';
import { getCurrentTenantNavigationConfig } from '../tenant/navigation_configs';
import profilePage from 'refactored/pages/profilePage';
import { LandingPage, LandingLoginPage, LandingRecoverPasswordPage } from 'pages/Landing/Landing';

const RedirectToDefault = () => <Redirect to={routes.default} />;

const enhance = compose(
  connect((state) => ({
    systemIsDown: statusSelectors.getSystemIsDown(state),
    country: settingsSelectors.getCountrySettings(state)?.country,
  }))
);

const PageView = ({ location }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      analyticsActions.sendEvent(analyticsEvents.pageView({ location }))
    );
  }, [location, dispatch]);

  return null;
};

const CloseMainMenuOnPageChange = ({ location, history }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (history.action === 'PUSH') {
      setTimeout(() => {
        dispatch(uiActions.closeMainMenu());
      }, 0);
    }
  }, [location, history, dispatch]);

  return null;
};

const DefaultBaseRouteComponent = (props) => {
  if (isTenantByName('xpo')) {
    return <Redirect to={`${routes.emergencies}/`} />;
  }
  return <Redirect to={routes.orders.default} />;
};

const Navigation = ({ history, systemIsDown, country }) => {
  const showNewForm = useShowNewSoForm();
  const isXpo = useSelector((state) =>
    clientSelectors.getSpecificClientConfig(state, constants.IS_XPO)
  );

  const navigationConfig = getCurrentTenantNavigationConfig();

  if (systemIsDown) return <ErrorPage />;

  return (
    <ConnectedRouter history={history}>
      <>
        <Route path="*" component={PageView} />
        <Route path="*" component={CloseMainMenuOnPageChange} />
        <Switch>
          <Route exact path="/" component={DefaultBaseRouteComponent} />
          {isXpo && <Route path={routes.profile} component={profilePage} />}

          {!isXpo && (
            <Route path={`${routes.campaign}`} component={UtmCampaign} />
          )}
          {navigationConfig.registrationPage.SignUp.isEnabled && (
            <Route
              path={navigationConfig.registrationPage.SignUp.path}
              component={navigationConfig.registrationPage.SignUp.component}
            />
          )}
          {/*<Route path={`${routes.signup}/:userDataHash`} component={SignUp} />*/}
          <Route
            path={`${routes.loginRecover}/:recoverToken`}
            component={isXpo ? LandingRecoverPasswordPage : LoginRecover}
          />
          <Route 
            path={routes.loginRecover} 
            component={isXpo ? LandingRecoverPasswordPage : LoginRecover} />
          <Route
            path={`${routes.invitation}/:userInvitationToken`}
            component={Invitations}
          />
          <Route path={routes.login} component={isXpo ? LandingPage : Login} />
          <Route path={routes.loginAccess} component={LandingLoginPage} />
          <Route path={routes.logout} component={Logout} />
          {!isXpo && (
            <Route
              path={`${routes.orders.active}/:orderId`}
              component={OrderDetails}
            />
          )}
          {!isXpo && (
            <Route
              path={`${routes.orders.previous}/:orderId`}
              component={OrderDetails}
            />
          )}
          {!isXpo && (
            <Route
              path={`${routes.orders.search}/:orderId`}
              component={OrderDetails}
            />
          )}
          {!isXpo && (
            <Route
              path={`${routes.orders.recurring}/:orderId`}
              component={OrderDetails}
            />
          )}
          {!isXpo && (
            <Route path={routes.orders.default} component={OrdersPage} />
          )}
          {!IS_TENANT && showNewForm && (
            <Route path={routes.newOrderCatalog} component={NewShipperOrder} />
          )}
          {!IS_TENANT && country !== 'UK' && (
            <Route path={routes.newOrder} component={NewOrder} />
          )}
          {!IS_TENANT && <Route path={routes.summary} component={Summary} />}
          {!IS_TENANT && (
            <Route path={routes.creditcard} component={CreditCard} />
          )}
          {!IS_TENANT && (
            <Route path={routes.paymentdata} component={PaymentData} />
          )}
          {!isXpo && <Route path={routes.addresses} component={Addresses} />}
          {!IS_TENANT && (
            <Route
              path={routes.organizationMembers}
              component={OrganizationMembers}
            />
          )}
          {!isXpo && <Route path={routes.billing} component={Billing} />}
          {!isXpo && <Route path={routes.settings} component={Settings} />}
          {!isXpo && <Route path={routes.tracking} component={TrackingPage} />}
          {isXpo && (
            <Route path={routes.emergencies} component={EmergenciesModule} />
          )}

          {SERVICES.SUPERSET && (
            <Route path={routes.dashboard} component={DashboardPage} />
          )}
          <Route component={RedirectToDefault} />
        </Switch>
      </>
    </ConnectedRouter>
  );
};

Navigation.propTypes = {
  history: PropTypes.any,
};

Navigation.defaultProps = {
  history: '',
};

export default enhance(Navigation);
